import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Trip } from '../class/TripsHistory';
import { DateHelper } from '../utils/DateHelper';
import DurationPipe from '../utils/pipes/duration.pipe';
import DynamicDatePipe from '../utils/pipes/dynamic-date.pipe';
import ImperialPipe from '../utils/pipes/imperial.pipe';
import TemperaturePipe from '../utils/pipes/temperature.pipe';
import { ConnectionService } from './connection.service';
import { DevicesManagerService } from './devices-manager.service';
import { PoiHistoryService } from './poihistory.service';
import { SaveListService } from './savelist.service';
import { TripService } from './trip.service';

interface ColumnDefinition {
  header: () => string;
  value: (trip: Trip) => string | number;
  condition?: () => boolean;
  /**
   * dataKey of the equivalent column in history-table's column chooser (see history-table.component.ts
   * `_buildColumns()`). When set, the column is only exported if that dataKey is currently selected in
   * the table, so exports stay in sync with what the user chose to display.
   * Left undefined for export-only columns that have no equivalent in the table (e.g. registration,
   * POI landmark): those always stay in the export.
   */
  dataKey?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceSaveListService extends SaveListService {
  public canva: unknown;
  public data: unknown;

  private _devicesManagerService = inject(DevicesManagerService);
  public tripService = inject(TripService);
  public poiHistoryService = inject(PoiHistoryService);
  public connectionService = inject(ConnectionService);
  private imperialPipe = inject(ImperialPipe);
  private durationPipe = inject(DurationPipe);
  private translate = inject(TranslateService);
  private dynamicDatePipe = inject(DynamicDatePipe);
  private temperaturePipe = inject(TemperaturePipe);

  private defaultValue(item): unknown {
    return item === undefined || item === null ? '' : item;
  }

  public getGraphData(
    title: string,
    service: string,
    breaking: string,
    labels: object,
    data: { data: number[]; label: string }[],
    unit: string,
    from: string,
    to: string,
    compare: boolean
  ) {
    const dataDetails = [];
    function full(item) {
      return item === undefined ? ' ' : item;
    }

    if (compare === true) {
      dataDetails.push([`"${title} de ${service} par ${breaking}"`], ['" "'], ['" "']);
      dataDetails.push([`"${full(breaking)}"`, `"${full(data[0].label)}"`, `"${full(data[1].label)}"`]);
      for (const i in labels) {
        dataDetails.push([
          `"${full(labels[i])}"`,
          `"${full(data[0].data[i])}${full(unit)}"`,
          `"${full(data[1].data[i])}${full(unit)}"`,
        ]);
      }
    } else {
      dataDetails.push(
        [`"${title} de ${service} par ${breaking}" (du ${from} au ${to})`],
        ['" "'],
        ['" "'],
        [`"${breaking}"`, `"${title}"`]
      );
      for (const i in labels) {
        dataDetails.push([`"${full(labels[i])}"`, `"${full(data[0].data[i])}${full(unit)}"`]);
      }
    }
    return dataDetails;
  }
  // TODO: reduce its Cognitive Complexity
  getTripsList(content: string) {
    if (content === 'poi') {
      const allPois = this.poiHistoryService.$poisHistory();
      const tripsDetails = [
        [
          '"N°"',
          `${this.translate.instant('IMPORT.REGISTRATION')}`,
          `${this.translate.instant('TRIP.DEPARTURE')}`,
          `${this.translate.instant('TRIP.ARRIVAL')}`,
          `${this.translate.instant('TRIP.DURATION')}`,
          `${this.translate.instant('TRIP.DISTANCE')}`,
          `${this.translate.instant('MAP.TOTALDISTANCE')}`,
          `${this.translate.instant('TRIP.STANDBY')}`,
          `${this.translate.instant('TRIP.ENGINETIME')}`,
          `${this.translate.instant('TRIP.STANDBYADDRESS')}`,
          `${this.translate.instant('TRIP.NEXTDEPARTURE')}`,
          `${this.translate.instant('DEVICE.API_ID')}`,
        ],
      ];
      for (const i in allPois) {
        const registration = allPois[i].registration !== null ? allPois[i].registration : 'N/A';
        const startDate =
          allPois[i].start !== undefined
            ? this.dynamicDatePipe.transform(new Date(allPois[i].start * 1000), 'medium')
            : '';
        const stopDate =
          allPois[i].stop !== undefined
            ? this.dynamicDatePipe.transform(new Date(allPois[i].stop * 1000), 'medium')
            : '';
        const tripTime = this.durationPipe.transform((allPois[i].stop - allPois[i].start) * 1000);
        const allMileage = this.poiHistoryService.distanceCount()[i];
        const nextStart =
          allPois[i].next_start !== undefined
            ? this.dynamicDatePipe.transform(new Date(allPois[i].next_start * 1000), 'medium')
            : '';
        const standBy =
          allPois[i].next_start !== undefined
            ? this.durationPipe.transform(allPois[i].next_start * 1000 - allPois[i].stop * 1000)
            : '';
        const engineTime = this.durationPipe.transform(allPois[i].engine * 1000);

        tripsDetails.push([
          `"${this.defaultValue(allPois[i].uid)}"`,
          `"${registration}"`,
          `"${startDate}"`,
          `"${stopDate}"`,
          `"${tripTime}"`,
          `"${this.defaultValue(allPois[i].distance_traveled)} km"`,
          `"${allMileage.toFixed(2)} km"`,
          `"${standBy}"`,
          `"${engineTime}"`,
          `"${this.defaultValue(this._landmarksService.$landmarksById()[allPois[i].uid_poi].label)}"`,
          `"${nextStart}"`,
          `"${this.defaultValue(allPois[i].uid_device)}"`,
        ]);
      }

      return tripsDetails;
    } else if (content === 'poiUnknown') {
      const allPois = this.poiHistoryService.$poisHistory();
      const tripsDetails = [
        [
          '"N°"',
          `${this.translate.instant('IMPORT.REGISTRATION')}`,
          `${this.translate.instant('TRIP.DEPARTURE')}`,
          `${this.translate.instant('TRIP.ARRIVAL')}`,
          `${this.translate.instant('TRIP.DURATION')}`,
          `${this.translate.instant('TRIP.DISTANCE')}`,
          `${this.translate.instant('MAP.TOTALDISTANCE')}`,
          `${this.translate.instant('TRIP.STANDBY')}`,
          `${this.translate.instant('TRIP.ENGINETIME')}`,
          `${this.translate.instant('TRIP.NEXTDEPARTURE')}`,
          `${this.translate.instant('DEVICE.API_ID')}`,
        ],
      ];
      for (const i in allPois) {
        const registration = allPois[i].registration !== null ? allPois[i].registration : 'N/A';
        const startDate =
          allPois[i].start !== undefined
            ? this.dynamicDatePipe.transform(new Date(allPois[i].start * 1000), 'medium')
            : '';
        const stopDate =
          allPois[i].stop !== undefined
            ? this.dynamicDatePipe.transform(new Date(allPois[i].stop * 1000), 'medium')
            : '';
        const tripTime = this.durationPipe.transform((allPois[i].stop - allPois[i].start) * 1000);
        const allMileage = this.poiHistoryService.distanceCount()[i];
        const nextStart =
          allPois[i].next_start !== undefined
            ? this.dynamicDatePipe.transform(new Date(allPois[i].next_start * 1000), 'medium')
            : '';
        const standBy =
          allPois[i].next_start !== undefined
            ? this.durationPipe.transform(allPois[i].next_start * 1000 - allPois[i].stop * 1000)
            : '';
        const engineTime = this.durationPipe.transform(allPois[i].engine * 1000);

        tripsDetails.push([
          `"${this.defaultValue(allPois[i].uid)}"`,
          `"${registration}"`,
          `"${startDate}"`,
          `"${stopDate}"`,
          `"${tripTime}"`,
          `"${this.defaultValue(allPois[i].distance_traveled)} km"`,
          `"${allMileage.toFixed(2)} km"`,
          `"${standBy}"`,
          `"${engineTime}"`,
          `"${nextStart}"`,
          `"${this.defaultValue(allPois[i].uid_device)}"`,
        ]);
      }
      return tripsDetails;
    }
  }

  /**
   * @param selectedColumns dataKeys currently selected in the history table's column chooser. When
   * provided, columns tied to a table dataKey (see `ColumnDefinition.dataKey`) are only exported if
   * they are part of this list, keeping CSV/Excel exports in sync with the table's selected columns.
   * Columns with no `dataKey` (export-only columns) are always exported regardless of this list.
   */
  exportTripsDevice(selectedColumns?: string[]): string[][] {
    const allTrips = this.tripService.$selectedTrips()?.length
      ? this.tripService.$selectedTrips()
      : this.tripService.$trips().trips;
    const activeColumns = this.buildTripColumns(allTrips).filter(
      col =>
        (col.condition === undefined || col.condition()) &&
        (col.dataKey === undefined || selectedColumns === undefined || selectedColumns.includes(col.dataKey))
    );

    const headers = activeColumns.map(col => col.header());
    const rows = allTrips.map(trip => activeColumns.map(col => String(col.value(trip))));

    return [headers, ...rows];
  }

  private buildTripColumns(allTrips: Trip[]): ColumnDefinition[] {
    const hasDeviceEngine = (trip: Trip) =>
      this._devicesManagerService.get(trip.deviceUid)?.device_type === 1;

    return [
      {
        header: () => this.translate.instant('DEVICE.API_ID'),
        value: trip => `${this.defaultValue(trip.deviceUid)}`,
        dataKey: 'deviceUid',
      },
      {
        header: () => this.translate.instant('IMPORT.REGISTRATION'),
        value: trip =>
          `${this.defaultValue(this._devicesManagerService.get(trip.deviceUid).registration)}`,
      },
      {
        header: () => this.translate.instant('TRIP.DEVICE'),
        value: trip =>
          `${this.defaultValue(this._devicesManagerService.get(trip.duplicateDeviceUid)?.registration)}`,
        dataKey: 'duplicateDeviceUid',
      },
      {
        header: () => this.translate.instant('COMMON.DRIVER'),
        value: trip => trip.driverName ?? 'N/A',
        dataKey: 'driverName',
      },
      {
        header: () => this.translate.instant('TRIP.DEPARTURE'),
        value: trip =>
          trip.startPoint.timestamp
            ? this._dynamicDatePipe.transform(new DateHelper(trip.startPoint.timestamp), 'short')
            : '',
        dataKey: 'startPoint.timestamp',
      },
      {
        header: () => this.translate.instant('TRIP.DEPARTURE_ADDRESS'),
        value: trip => (trip.hasPrivacy ? 'Privacy' : `${this.defaultValue(trip.startPoint.address)}`),
        dataKey: 'startPoint.address',
      },
      {
        header: () => this.translate.instant('TRIP.ARRIVAL'),
        value: trip =>
          trip.stopPoint.timestamp
            ? this._dynamicDatePipe.transform(new DateHelper(trip.stopPoint.timestamp), 'short')
            : '',
        dataKey: 'stopPoint.timestamp',
      },
      {
        header: () => this.translate.instant('TRIP.STANDBYADDRESS'),
        value: trip => (trip.hasPrivacy ? 'Privacy' : `${this.defaultValue(trip.stopPoint.address)}`),
        dataKey: 'stopPoint.address',
      },
      {
        header: () => this.translate.instant('TRIP.DURATION'),
        value: trip =>
          this.durationPipe.transform(
            trip.stopPoint.timestamp * 1000 - trip.startPoint.timestamp * 1000,
            'sec'
          ),
        dataKey: 'duration',
      },
      {
        header: () => this.translate.instant('TRIP.DISTANCE'),
        value: trip => `${this.defaultValue(trip.mileage)} km`,
        dataKey: 'mileage',
      },
      {
        header: () => this.translate.instant('TRIP.STANDBY'),
        value: trip => this.durationPipe.transform(trip.stopDuration * 1000, 'sec'),
        dataKey: 'stopDuration',
      },
      {
        header: () => this.translate.instant('DEVICE.TYPE_ENGINE'),
        value: trip =>
          this.translate.instant(
            'DEVICE.' + (trip.stopPoint?.isEngineRunning ? 'ENGINE_RUNNING' : 'ENGINE_FULLY_STOPPED')
          ),
        condition: () => allTrips.some(trip => !hasDeviceEngine(trip)),
        dataKey: 'stopType',
      },
      {
        header: () => this.translate.instant('HISTORY.FUEL_CARDS'),
        value: trip => `${this.defaultValue(trip.stopPoint.gasStationEvent)}`,
        dataKey: 'stopPoint.gasStationEvent',
      },
      {
        header: () => this.translate.instant('TRIP.ENGINETIME'),
        value: trip => this.durationPipe.transform(trip.engine * 1000, 'sec'),
        dataKey: 'engine',
      },
      {
        header: () => this.translate.instant('TRIP.SENSOR'),
        value: trip => this.durationPipe.transform(trip.input * 1000, 'sec'),
        dataKey: 'input',
      },
      // no table equivalent (not part of the column chooser): always exported.
      {
        header: () => this.translate.instant('POI.LANDMARK'),
        value: trip => {
          if (trip.hasPrivacy) return 'Privacy';
          if (trip.stopPoint.pois && this._landmarksService.$landmarksById()?.[trip.stopPoint.pois?.[0]]) {
            return `${this.defaultValue(this._landmarksService.$landmarksById()[trip.stopPoint.pois[0]]?.label)}`;
          }
          if (trip.stopPoint.uid_zoi && this._landmarksService.$landmarksById()?.[trip.stopPoint.uid_zoi]) {
            return `${this.defaultValue(this._landmarksService.$landmarksById()[trip.stopPoint.uid_zoi]?.label)}`;
          }
          return '';
        },
      },
      {
        header: () => this.translate.instant('TRIP.AVERAGESPEED'),
        value: trip =>
          trip.hasPrivacy ? 'Privacy' : this.imperialPipe.transform(trip.speedAverage, 'speed', 0),
        dataKey: 'speedAverage',
      },
      {
        header: () => this.translate.instant('TRIP.MAX_SPEED'),
        value: trip =>
          trip.hasPrivacy ? 'Privacy' : this.imperialPipe.transform(trip?.speed_max ?? 0, 'speed', 0),
        dataKey: 'speed_max',
      },
      {
        header: () => this.translate.instant('TRIP.ECODRIVING'),
        value: trip => `${trip.ecoScoring} / 100`,
        dataKey: 'ecoScoring',
      },
      {
        header: () => this.translate.instant('TRIP.REAL_CONSUMPTION'),
        value: trip => (trip.realFuelConsumption ? `${trip.realFuelConsumption} L` : 'N/A'),
        dataKey: 'realFuelConsumption',
      },
      ...this._buildTemperatureColumns(),
    ];
  }

  /**
   * One column per temperature sensor, matching history-table's dynamic `_getTemperatureCols()`
   * (dataKey `temperatures-${sensor.uid}`, header = the sensor's own label).
   */
  private _buildTemperatureColumns(): ColumnDefinition[] {
    const temperatureSensors = this.tripService.$trips()?.temperatureSensors;
    if (!temperatureSensors) return [];

    return Object.values(temperatureSensors).map(sensor => ({
      header: () => this.translate.instant(sensor.label),
      value: trip =>
        `${this.temperaturePipe.transform(trip.startPoint?.temperatures?.[sensor.uid]?.value)} | ${this.temperaturePipe.transform(trip.stopPoint?.temperatures?.[sensor.uid]?.value)}`,
      dataKey: `temperatures-${sensor.uid}`,
    }));
  }
}
